"use client";

import { useEffect } from "react";
import { useRouter, usePathname } from "next/navigation";
import { useAppDispatch } from "lib/redux/hooks";
import { setAccessToken, setIsPremiumUser, setSubscriptionCreditCount } from "lib/redux/uiSlice";
import { googleOneTapSignIn } from "api/googleOneTapApi";
import { getUserProfile } from "api/userApi";
import { getSubscriptionInfo } from "api/subscription/subscriptionInfo";
import { initializeGAUserTracking } from "lib/hooks/useGAUserTracking";
import { toast } from "react-toastify";
import { getRedirectPage } from "lib/utils/getRedirectPage";
export default function OneTapTopRight() {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const pathname = usePathname();
  useEffect(() => {
    // Only run on client side
    if (typeof window === "undefined") return;

    // Don't show if user is already logged in
    if (localStorage.getItem("access-token")) {
      // console.log("[One Tap] User already logged in, skipping");
      return;
    }

    // Pages where One Tap should NOT be shown (to avoid interrupting specific flows)
    const excludedPages = ["/extension/connect",
    // Extension authentication flow
    "/referral",
    // Referral flow
    "/affiliate",
    // Affiliate flow
    "/oauth2/redirect"];

    // Check if current page should be excluded
    if (pathname && excludedPages.includes(pathname)) {
      // console.log("[One Tap] Skipping on excluded page:", pathname);
      return;
    }
    if (pathname) {
      localStorage.setItem("current-page", pathname);
      // console.log("[One Tap] Saved current page:", pathname);
    }

    // Wait for Google Identity Services to load
    const waitForGsi = async () => {
      for (let i = 0; i < 50; i++) {
        // @ts-ignore
        if (window.google?.accounts?.id) return true;
        await new Promise(r => setTimeout(r, 100));
      }
      return false;
    };
    const handleCredentialResponse = async (response: any) => {
      const credential = response?.credential;
      if (!credential) {
        // console.error("[One Tap] No credential received");
        return;
      }
      try {
        const {
          accessToken
        } = await googleOneTapSignIn(credential);
        if (!accessToken) {
          console.error("[One Tap] No access token received from backend");
          return;
        }
        localStorage.setItem("access-token", accessToken);
        dispatch(setAccessToken(accessToken));

        // Fetch user profile for GA tracking (needed before redirect for correct user ID)
        try {
          const userProfile = await getUserProfile();
          if (userProfile && userProfile.id) {
            initializeGAUserTracking(userProfile.id, dispatch);
          }
        } catch (profileError) {
          // Continue even if profile fetch fails
        }

        // Redirect IMMEDIATELY – don't block on subscription info.
        // The subscription fetch had to wait for a full extra round-trip before
        // the router.push, causing noticeable delay (Issue #4).
        // The target page's ResumeBuilderComponent already fetches subscription
        // info independently on mount, so this is safe.
        const currentPage = localStorage.getItem("current-page");
        const redirectPage = getRedirectPage(currentPage);
        // If already on the destination page, skip the router.push entirely –
        // there's no need for a navigation, and avoiding it means layout/form
        // state is preserved. The Redux updates above (setAccessToken, etc.)
        // are already reactive, so the UI (sign-in → sign-out, premium button)
        // updates without any navigation.
        if (redirectPage !== pathname) {
          router.push(redirectPage);
        }

        // Fetch subscription info in the background AFTER redirect is initiated.
        // The dispatches will still reach the Redux store (shared singleton across
        // Next.js client-side navigations) and update any already-mounted components.
        getSubscriptionInfo().then(subscriptionInfo => {
          dispatch(setSubscriptionCreditCount(subscriptionInfo.credits));
          dispatch(setIsPremiumUser(subscriptionInfo.premium));
        }).catch(subscriptionError => {
          console.error("[One Tap] Failed to fetch subscription info:", subscriptionError);
        });
      } catch (error: any) {
        // console.error("[One Tap] Authentication failed:", error);
        toast.error("Server Error. Please try again.");
        // Show user-friendly error message
        if (error.response?.status === 401) {
          console.error("[One Tap] Invalid credential - verification failed");
        } else {
          // console.error("[One Tap] Network or server error");
        }
      }
    };

    // Initialize Google One Tap
    const initializeOneTap = async () => {
      const gsiLoaded = await waitForGsi();
      if (!gsiLoaded) {
        // console.warn("[One Tap] Google Identity Services not loaded");
        return;
      }

      // @ts-ignore
      const gsi = window.google.accounts.id;

      // Initialize with callback
      gsi.initialize({
        client_id: "31384632577-8baskiv1ckophcdnuhaq3d0q35vr7bvp.apps.googleusercontent.com",
        callback: handleCredentialResponse,
        auto_select: false,
        // Don't auto-select - let user choose
        cancel_on_tap_outside: true // Dismiss if user clicks outside
      });

      // Show the One Tap prompt
      gsi.prompt((notification: any) => {
        // Handle prompt notifications
        if (notification.isNotDisplayed()) {
          // console.log("[One Tap] Prompt not displayed:", notification.getNotDisplayedReason());
        } else if (notification.isSkippedMoment()) {
          // console.log("[One Tap] Prompt skipped:", notification.getSkippedReason());
        } else if (notification.isDismissedMoment()) {
          // console.log("[One Tap] Prompt dismissed:", notification.getDismissedReason());
        }
      });
    };
    initializeOneTap();
  }, [router, dispatch, pathname]);
  return null;
}